import { useEffect, useState } from 'react'
import styled from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import { Spacings } from '@styles'

import { DrawerBodyWrapper, DrawerFooterWrapper } from '@base'
import { CRAN_SECTION_TYPES } from '@constants'
import { getCranReportById } from '@data/conn/get/cranReport'
import { getLatestCommentImagePaths } from '../../../cranCommentImages'
import { showLoader } from '@data/state/action'
import { useForceUpdate } from '@hooks'
import { addCommentInCranReport } from '@data/conn/post/cranReport'

import CranCommentCard from '../Components/CranCommentCard'

// Grey body so the white comment cards stand out against it.
const DrawerBody = styled(DrawerBodyWrapper)`
	background: ${({ theme }) => theme.colors.surface.page};
`

// Each comment sits in its own card, spaced apart.
const FieldWrapper = styled.div`
	& + & {
		margin-top: ${Spacings.SPACING_3B};
	}
`

const dataToForm = (prevState, data) => {
	if (data === null) {
		return prevState
	}
	let _obj = data?.slice(-1)?.[0]
	return {
		...prevState,
		value: _obj?.comment,
		show_in_cran: _obj?.show_in_cran,
		comment_type: _obj?.comment_type,
		// Documents of the same (last) entry the comment itself comes from.
		documents: getLatestCommentImagePaths(data),
		dirty: true,
	}
}

const deriveFormState = (data) => {
	const state = {
		auditor: {
			label: 'Auditor Comment',
			placeholder: 'Enter comments here...',
			value: '',
			show_in_cran: false,
			comment_type: null,
			disabled: false,
			dirty: false,
		},
		general_financials: {
			label: 'General Comments on Financials',
			placeholder: 'Enter general comments on financials',
			value: '',
			show_in_cran: false,
			comment_type: null,
			disabled: false,
			dirty: false,
		},
		revenue_bifurcation: {
			label: 'Revenue and Revenue Bifurcation',
			placeholder: 'Enter comments here...',
			value: '',
			show_in_cran: false,
			comment_type: null,
			disabled: false,
			dirty: false,
		},
		profitability: {
			label: 'Profitability',
			placeholer: 'Enter comments here',
			value: '',
			show_in_cran: false,
			comment_type: null,
			disabled: false,
			dirty: false,
		},
		leverage: {
			label: 'Leverage',
			placeholer: 'Enter comments here',
			value: '',
			show_in_cran: false,
			comment_type: null,
			disabled: false,
			dirty: false,
		},
		coverage: {
			label: 'Coverage',
			placeholer: 'Enter comments here',
			value: '',
			show_in_cran: false,
			comment_type: null,
			disabled: false,
			dirty: false,
		},
		liquidity: {
			label: 'Liquidity',
			placeholer: 'Enter comments here',
			value: '',
			show_in_cran: false,
			comment_type: null,
			disabled: false,
			dirty: false,
		},
		cash_flows: {
			label: 'Cash Flows',
			placeholer: 'Enter comments here',
			value: '',
			show_in_cran: false,
			comment_type: null,
			disabled: false,
			dirty: false,
		},
		forex_exposure: {
			label: 'Foreign Exchange Exposure',
			placeholer: 'Enter comments here',
			value: '',
			show_in_cran: false,
			comment_type: null,
			disabled: false,
			dirty: false,
		},
		interest_rate_sensitivity: {
			label: 'Interest Rate Sensitivity',
			placeholer: 'Enter comments here',
			value: '',
			show_in_cran: false,
			comment_type: null,
			disabled: false,
			dirty: false,
		},
		operating_cycle: {
			label: 'Operating Cycle',
			placeholer: 'Enter comments here',
			value: '',
			show_in_cran: false,
			comment_type: null,
			disabled: false,
			dirty: false,
		},
		interest_rate_risk: {
			label: 'Interest Rate Risk',
			placeholer: 'Enter comments here',
			value: '',
			show_in_cran: false,
			comment_type: null,
			disabled: false,
			dirty: false,
		},
		fund_access: {
			label: 'Access to finance and other means of fund',
			placeholer: 'Enter comments here',
			value: '',
			show_in_cran: false,
			comment_type: null,
			disabled: false,
			dirty: false,
		},
		forex_risk: {
			label: 'Forex Risk',
			placeholer: 'Enter comments here',
			value: '',
			show_in_cran: false,
			comment_type: null,
			disabled: false,
			dirty: false,
		},
		accounting_quality: {
			label: 'Accounting Quality',
			placeholer: 'Enter comments here',
			value: '',
			show_in_cran: false,
			comment_type: null,
			disabled: false,
			dirty: false,
		},
		contingent_liabilities: {
			label: 'Contingent Liabilities',
			placeholer: 'Enter comments here',
			value: '',
			show_in_cran: false,
			comment_type: null,
			disabled: false,
			dirty: false,
		},
	}
	if (data) {
		state.auditor = dataToForm(state.auditor, data?.auditor)
		state.general_financials = dataToForm(state.general_financials, data?.general_financials)
		state.revenue_bifurcation = dataToForm(state.revenue_bifurcation, data?.revenue_bifurcation)
		state.profitability = dataToForm(state.profitability, data?.profitability)
		state.leverage = dataToForm(state.leverage, data?.leverage)
		state.coverage = dataToForm(state.coverage, data?.coverage)
		state.liquidity = dataToForm(state.liquidity, data?.liquidity)
		state.cash_flows = dataToForm(state.cash_flows, data?.cash_flows)
		state.forex_exposure = dataToForm(state.forex_exposure, data?.forex_exposure)
		state.interest_rate_sensitivity = dataToForm(state.interest_rate_sensitivity, data?.interest_rate_sensitivity)
		state.operating_cycle = dataToForm(state.operating_cycle, data?.operating_cycle)
		state.interest_rate_risk = dataToForm(state.interest_rate_risk, data?.interest_rate_risk)
		state.fund_access = dataToForm(state.fund_access, data?.fund_access)
		state.forex_risk = dataToForm(state.forex_risk, data?.forex_risk)
		state.accounting_quality = dataToForm(state.accounting_quality, data?.accounting_quality)
		state.contingent_liabilities = dataToForm(state.contingent_liabilities, data?.contingent_liabilities)
	}

	return state
}

const FinancialsRiskForm = ({ probeId }) => {
	const dispatch = useDispatch()
	const [formState, setFormState] = useState(deriveFormState())
	const loggedInUser = useSelector((state) => state.getIn(['user', 'user']))
	const [forceUpdate, update] = useForceUpdate()

	useEffect(() => {
		dispatch(showLoader(true))
		getCranReportById(probeId, [CRAN_SECTION_TYPES.FINANCIALS])
			.then((response) => {
				setFormState(deriveFormState(response))
			})
			.catch((error) => {
				console.log(error)
			})
			.finally(() => {
				dispatch(showLoader(false))
			})
	}, [forceUpdate])

	const updateState = (key) => (value) => {
		setFormState((_s) => ({
			..._s,
			[key]: {
				..._s[key],
				value: value,
			},
		}))
	}

	// Every change to a comment — its text, its CRAN visibility or its sentiment — is saved with
	// the same endpoint, so the card never holds an unsaved state.
	const saveComment = (key, overrides = {}) => {
		const obj = { ...formState[key], ...overrides }
		const comment = {
			comment: obj.value,
			comment_type: obj.comment_type,
			created_by: loggedInUser?.user_id,
			show_in_cran: obj?.show_in_cran,
		}
		dispatch(showLoader(true))
		addCommentInCranReport({
			userId: loggedInUser?.user_id,
			commentValue: comment,
			commentKey: key,
			probe42Id: probeId,
		})
			.then((response) => {})
			.catch((error) => {})
			.finally(() => {
				update()
				dispatch(showLoader(false))
			})
	}

	const updateCheckHandler = (key) => (val) => {
		setFormState((_s) => ({
			..._s,
			[key]: {
				..._s[key],
				show_in_cran: val,
			},
		}))
		saveComment(key, { show_in_cran: val })
	}

	const commentTypeHandler = (key) => (value) => {
		setFormState((_s) => ({
			..._s,
			[key]: {
				..._s[key],
				comment_type: value,
			},
		}))
		saveComment(key, { comment_type: value })
	}

	// `value` is the draft the editor just saved; it is also written back to the form so the card
	// shows the new comment immediately, before the refetch lands.
	const addHandler = (key) => (value) => {
		const comment = value ?? formState[key]?.value
		updateState(key)(comment)
		saveComment(key, { value: comment })
	}

	return (
		<>
			<DrawerBody>
				<FieldWrapper>
					<CranCommentCard
						label={formState?.auditor?.label}
						placeholder={formState?.auditor?.placeholder}
						toggleState={formState?.auditor?.comment_type}
						savedDocuments={formState?.auditor?.documents}
						textValue={formState?.auditor?.value}
						onCheckHandler={updateCheckHandler('auditor')}
						checkValue={formState?.auditor?.show_in_cran}
						onToggleChange={commentTypeHandler('auditor')}
						onButtonClick={addHandler('auditor')}
						disabled={formState?.auditor?.disabled}
						probeId={probeId}
						commentKey={'auditor'}
					/>
				</FieldWrapper>
				<FieldWrapper>
					<CranCommentCard
						label={formState?.general_financials?.label}
						placeholder={formState?.general_financials?.placeholder}
						toggleState={formState?.general_financials?.comment_type}
						savedDocuments={formState?.general_financials?.documents}
						textValue={formState?.general_financials?.value}
						onCheckHandler={updateCheckHandler('general_financials')}
						checkValue={formState?.general_financials?.show_in_cran}
						onToggleChange={commentTypeHandler('general_financials')}
						onButtonClick={addHandler('general_financials')}
						disabled={formState?.general_financials?.disabled}
						probeId={probeId}
						commentKey={'general_financials'}
					/>
				</FieldWrapper>
				<FieldWrapper>
					<CranCommentCard
						label={formState?.revenue_bifurcation?.label}
						placeholder={formState?.revenue_bifurcation?.placeholder}
						toggleState={formState?.revenue_bifurcation?.comment_type}
						savedDocuments={formState?.revenue_bifurcation?.documents}
						textValue={formState?.revenue_bifurcation?.value}
						onCheckHandler={updateCheckHandler('revenue_bifurcation')}
						checkValue={formState?.revenue_bifurcation?.show_in_cran}
						onToggleChange={commentTypeHandler('revenue_bifurcation')}
						onButtonClick={addHandler('revenue_bifurcation')}
						disabled={formState?.revenue_bifurcation?.disabled}
						probeId={probeId}
						commentKey={'revenue_bifurcation'}
					/>
				</FieldWrapper>
				<FieldWrapper>
					<CranCommentCard
						label={formState?.profitability?.label}
						placeholder={formState?.profitability?.placeholder}
						toggleState={formState?.profitability?.comment_type}
						savedDocuments={formState?.profitability?.documents}
						textValue={formState?.profitability?.value}
						onCheckHandler={updateCheckHandler('profitability')}
						checkValue={formState?.profitability?.show_in_cran}
						onToggleChange={commentTypeHandler('profitability')}
						onButtonClick={addHandler('profitability')}
						disabled={formState?.profitability?.disabled}
						probeId={probeId}
						commentKey={'profitability'}
					/>
				</FieldWrapper>
				<FieldWrapper>
					<CranCommentCard
						label={formState?.leverage?.label}
						placeholder={formState?.leverage?.placeholder}
						toggleState={formState?.leverage?.comment_type}
						savedDocuments={formState?.leverage?.documents}
						textValue={formState?.leverage?.value}
						onCheckHandler={updateCheckHandler('leverage')}
						checkValue={formState?.leverage?.show_in_cran}
						onToggleChange={commentTypeHandler('leverage')}
						onButtonClick={addHandler('leverage')}
						disabled={formState?.leverage?.disabled}
						probeId={probeId}
						commentKey={'leverage'}
					/>
				</FieldWrapper>
				<FieldWrapper>
					<CranCommentCard
						label={formState?.coverage?.label}
						placeholder={formState?.coverage?.placeholder}
						toggleState={formState?.coverage?.comment_type}
						savedDocuments={formState?.coverage?.documents}
						textValue={formState?.coverage?.value}
						onCheckHandler={updateCheckHandler('coverage')}
						checkValue={formState?.coverage?.show_in_cran}
						onToggleChange={commentTypeHandler('coverage')}
						onButtonClick={addHandler('coverage')}
						disabled={formState?.coverage?.disabled}
						probeId={probeId}
						commentKey={'coverage'}
					/>
				</FieldWrapper>
				<FieldWrapper>
					<CranCommentCard
						label={formState?.liquidity?.label}
						placeholder={formState?.liquidity?.placeholder}
						toggleState={formState?.liquidity?.comment_type}
						savedDocuments={formState?.liquidity?.documents}
						textValue={formState?.liquidity?.value}
						onCheckHandler={updateCheckHandler('liquidity')}
						checkValue={formState?.liquidity?.show_in_cran}
						onToggleChange={commentTypeHandler('liquidity')}
						onButtonClick={addHandler('liquidity')}
						disabled={formState?.liquidity?.disabled}
						probeId={probeId}
						commentKey={'liquidity'}
					/>
				</FieldWrapper>
				<FieldWrapper>
					<CranCommentCard
						label={formState?.cash_flows?.label}
						placeholder={formState?.cash_flows?.placeholder}
						toggleState={formState?.cash_flows?.comment_type}
						savedDocuments={formState?.cash_flows?.documents}
						textValue={formState?.cash_flows?.value}
						onCheckHandler={updateCheckHandler('cash_flows')}
						checkValue={formState?.cash_flows?.show_in_cran}
						onToggleChange={commentTypeHandler('cash_flows')}
						onButtonClick={addHandler('cash_flows')}
						disabled={formState?.cash_flows?.disabled}
						probeId={probeId}
						commentKey={'cash_flows'}
					/>
				</FieldWrapper>
				<FieldWrapper>
					<CranCommentCard
						label={formState?.forex_exposure?.label}
						placeholder={formState?.forex_exposure?.placeholder}
						toggleState={formState?.forex_exposure?.comment_type}
						savedDocuments={formState?.forex_exposure?.documents}
						textValue={formState?.forex_exposure?.value}
						onCheckHandler={updateCheckHandler('forex_exposure')}
						checkValue={formState?.forex_exposure?.show_in_cran}
						onToggleChange={commentTypeHandler('forex_exposure')}
						onButtonClick={addHandler('forex_exposure')}
						disabled={formState?.forex_exposure?.disabled}
						probeId={probeId}
						commentKey={'forex_exposure'}
					/>
				</FieldWrapper>
				<FieldWrapper>
					<CranCommentCard
						label={formState?.interest_rate_sensitivity?.label}
						placeholder={formState?.interest_rate_sensitivity?.placeholder}
						toggleState={formState?.interest_rate_sensitivity?.comment_type}
						savedDocuments={formState?.interest_rate_sensitivity?.documents}
						textValue={formState?.interest_rate_sensitivity?.value}
						onCheckHandler={updateCheckHandler('interest_rate_sensitivity')}
						checkValue={formState?.interest_rate_sensitivity?.show_in_cran}
						onToggleChange={commentTypeHandler('interest_rate_sensitivity')}
						onButtonClick={addHandler('interest_rate_sensitivity')}
						disabled={formState?.interest_rate_sensitivity?.disabled}
						probeId={probeId}
						commentKey={'interest_rate_sensitivity'}
					/>
				</FieldWrapper>
				<FieldWrapper>
					<CranCommentCard
						label={formState?.operating_cycle?.label}
						placeholder={formState?.operating_cycle?.placeholder}
						toggleState={formState?.operating_cycle?.comment_type}
						savedDocuments={formState?.operating_cycle?.documents}
						textValue={formState?.operating_cycle?.value}
						onCheckHandler={updateCheckHandler('operating_cycle')}
						checkValue={formState?.operating_cycle?.show_in_cran}
						onToggleChange={commentTypeHandler('operating_cycle')}
						onButtonClick={addHandler('operating_cycle')}
						disabled={formState?.operating_cycle?.disabled}
						probeId={probeId}
						commentKey={'operating_cycle'}
					/>
				</FieldWrapper>
				<FieldWrapper>
					<CranCommentCard
						label={formState?.interest_rate_risk?.label}
						placeholder={formState?.interest_rate_risk?.placeholder}
						toggleState={formState?.interest_rate_risk?.comment_type}
						savedDocuments={formState?.interest_rate_risk?.documents}
						textValue={formState?.interest_rate_risk?.value}
						onCheckHandler={updateCheckHandler('interest_rate_risk')}
						checkValue={formState?.interest_rate_risk?.show_in_cran}
						onToggleChange={commentTypeHandler('interest_rate_risk')}
						onButtonClick={addHandler('interest_rate_risk')}
						disabled={formState?.interest_rate_risk?.disabled}
						probeId={probeId}
						commentKey={'interest_rate_risk'}
					/>
				</FieldWrapper>
				<FieldWrapper>
					<CranCommentCard
						label={formState?.fund_access?.label}
						placeholder={formState?.fund_access?.placeholder}
						toggleState={formState?.fund_access?.comment_type}
						savedDocuments={formState?.fund_access?.documents}
						textValue={formState?.fund_access?.value}
						onCheckHandler={updateCheckHandler('fund_access')}
						checkValue={formState?.fund_access?.show_in_cran}
						onToggleChange={commentTypeHandler('fund_access')}
						onButtonClick={addHandler('fund_access')}
						disabled={formState?.fund_access?.disabled}
						probeId={probeId}
						commentKey={'fund_access'}
					/>
				</FieldWrapper>
				<FieldWrapper>
					<CranCommentCard
						label={formState?.forex_risk?.label}
						placeholder={formState?.forex_risk?.placeholder}
						toggleState={formState?.forex_risk?.comment_type}
						savedDocuments={formState?.forex_risk?.documents}
						textValue={formState?.forex_risk?.value}
						onCheckHandler={updateCheckHandler('forex_risk')}
						checkValue={formState?.forex_risk?.show_in_cran}
						onToggleChange={commentTypeHandler('forex_risk')}
						onButtonClick={addHandler('forex_risk')}
						disabled={formState?.forex_risk?.disabled}
						probeId={probeId}
						commentKey={'forex_risk'}
					/>
				</FieldWrapper>
				<FieldWrapper>
					<CranCommentCard
						label={formState?.accounting_quality?.label}
						placeholder={formState?.accounting_quality?.placeholder}
						toggleState={formState?.accounting_quality?.comment_type}
						savedDocuments={formState?.accounting_quality?.documents}
						textValue={formState?.accounting_quality?.value}
						onCheckHandler={updateCheckHandler('accounting_quality')}
						checkValue={formState?.accounting_quality?.show_in_cran}
						onToggleChange={commentTypeHandler('accounting_quality')}
						onButtonClick={addHandler('accounting_quality')}
						disabled={formState?.accounting_quality?.disabled}
						probeId={probeId}
						commentKey={'accounting_quality'}
					/>
				</FieldWrapper>
				<FieldWrapper>
					<CranCommentCard
						label={formState?.contingent_liabilities?.label}
						placeholder={formState?.contingent_liabilities?.placeholder}
						toggleState={formState?.contingent_liabilities?.comment_type}
						savedDocuments={formState?.contingent_liabilities?.documents}
						textValue={formState?.contingent_liabilities?.value}
						onCheckHandler={updateCheckHandler('contingent_liabilities')}
						checkValue={formState?.contingent_liabilities?.show_in_cran}
						onToggleChange={commentTypeHandler('contingent_liabilities')}
						onButtonClick={addHandler('contingent_liabilities')}
						disabled={formState?.contingent_liabilities?.disabled}
						probeId={probeId}
						commentKey={'contingent_liabilities'}
					/>
				</FieldWrapper>
			</DrawerBody>
			<DrawerFooterWrapper></DrawerFooterWrapper>
		</>
	)
}

export default FinancialsRiskForm
